// Detalhe de UMA ligação (aberto ao clicar numa linha do Relatório de Ligações).
// Mostra as infos da chamada e — quando há gravação — um player de áudio custom
// (play/pause, barra de progresso arrastável, tempo e download). A URL da gravação
// é assinada e temporária: só é buscada quando o diálogo abre (getCallRecordingUrl).
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@organizacaox/lai-design-system";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Clock3,
  ExternalLink,
  FileText,
  Volume2,
} from "@organizacaox/lai-design-system/icons";
import { type CallListItem } from "./api.js";
import { useDispositions } from "@/hooks/use-dispositions";
import { useLead } from "@/hooks/use-leads";
import { useCallRecording, useCallTranscription } from "@/hooks/use-calls";
import { StatusBadge, formatDuration, formatDateTime, formatPhone } from "./calls-common.js";
import { AudioPlayer } from "./softphone/audio-player.js";
import { buildCallTimeline, formatTranscriptTime, normalizeTranscript } from "./call-review.js";
import { CallRecordingActions } from "./call-recording-actions.js";
import { CallAudioQualityPanel } from "./call-audio-quality.js";
import "./i18n.js";

// O lead do deep-link do HubSpot não muda no minuto em que o diálogo fica
// aberto — reabrir não precisa de outro request.
const LEAD_STALE_MS = 60_000;

// Uma linha do grid de infos. Renderiza só quando há valor (children).
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function CallDetailDialog({
  call,
  open,
  onOpenChange,
  canDeleteRecording = false,
}: {
  call: CallListItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  canDeleteRecording?: boolean;
}) {
  const { t } = useTranslation();
  // URL assinada só quando o diálogo abre e a ligação tem gravação.
  const recording = useCallRecording(call?.id, { enabled: open && !!call?.recordingName });

  // Transcrição só quando o diálogo abre e há gravação (dela deriva o texto).
  // Buscada à parte da listagem para não carregar texto longo em cada linha.
  const transcription = useCallTranscription(call?.id, {
    enabled: open && !!call?.recordingName,
  });

  // O lead do qual esta ligação saiu — buscado só para o deep-link do HubSpot,
  // que o back monta (portal + external id) e a ligação não carrega: a listagem
  // traz leadId/leadName e mais nada. Mesma chave da ficha do lead, então quem
  // já abriu a ficha reaproveita o cache. Sem leadId (ligação avulsa, acesso
  // temporário) a consulta nem sai.
  const lead = useLead(call?.leadId, {
    enabled: open && !!call?.leadId,
    staleTime: LEAD_STALE_MS,
  });

  // Rótulo da tabulação: mapeia dispositionId → label pela lista da org (cacheada).
  const { data: dispositions } = useDispositions({ enabled: open });
  const dispositionLabel = call?.dispositionId
    ? (dispositions?.find((d) => d.id === call.dispositionId)?.label ?? null)
    : call?.failureReason
      ? null
      : call?.status === "COMPLETED"
        ? t("calls.common.completedDisposition")
        : null;

  const inbound = call?.direction === "INBOUND";
  const number = inbound ? call?.callerDid : call?.targetPhone;
  const outHidden = call?.direction === "OUTBOUND" && call?.targetPhoneHidden;
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight;
  const timeline = call ? buildCallTimeline(call) : [];
  const transcriptLines = normalizeTranscript(
    transcription.data?.transcript?.length
      ? transcription.data.transcript
      : (transcription.data?.transcription ?? null),
    call?.duration,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t("ui.dismissDialog")}
        className="max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:h-[min(46rem,calc(100dvh-2rem))] sm:max-w-6xl"
      >
        {call && (
          <>
            <DialogHeader className="border-b border-border/70 px-5 py-4 pr-14 sm:px-6 sm:py-5 sm:pr-16">
              <DialogTitle className="flex flex-wrap items-center gap-2 text-lg">
                <Icon
                  className={
                    inbound
                      ? "size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                      : "size-4 shrink-0 text-sky-600 dark:text-sky-400"
                  }
                  aria-label={t(`calls.common.direction.${call.direction}`)}
                />
                <span className="tabular-nums">
                  {outHidden ? t("calls.common.hiddenNumber") : formatPhone(number ?? null)}
                </span>
                <StatusBadge status={call.status} />
                {call.channel === "WHATSAPP" && <Badge variant="outline">WhatsApp</Badge>}
              </DialogTitle>
            </DialogHeader>

            <div className="min-h-0 overflow-y-auto xl:overflow-hidden">
              <div
                data-testid="call-review-layout"
                className="grid min-h-full xl:h-full xl:grid-cols-[minmax(18rem,0.9fr)_minmax(14rem,0.65fr)_minmax(22rem,1.15fr)]"
              >
                <section
                  aria-label={t("callDetail.review")}
                  className="space-y-6 px-5 py-5 sm:px-6 xl:min-h-0 xl:overflow-y-auto"
                >
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <InfoRow label={t("callDetail.lead")}>
                      {call.leadName ?? <span className="text-muted-foreground">—</span>}
                    </InfoRow>
                    <InfoRow label={t("callDetail.owner")}>
                      {call.agentName ?? <span className="text-muted-foreground">—</span>}
                    </InfoRow>
                    <InfoRow label={t("callDetail.talkTime")}>
                      <span className="tabular-nums">{formatDuration(call.duration)}</span>
                    </InfoRow>
                    {/* Espera = do pedido de discagem até atenderem; "—" quando
                        ninguém atendeu (não houve espera *até* o atendimento). */}
                    <InfoRow label={t("callDetail.waitTime")}>
                      <span className="tabular-nums">{formatDuration(call.waitSeconds)}</span>
                    </InfoRow>
                    <InfoRow label={t("callDetail.startedAt")}>
                      <span className="tabular-nums">
                        {formatDateTime(call.startedAt ?? call.createdAt)}
                      </span>
                    </InfoRow>
                    <InfoRow label={t("callDetail.disposition")}>
                      {dispositionLabel ?? <span className="text-muted-foreground">—</span>}
                    </InfoRow>
                    {call.failureReason && (
                      <InfoRow label={t("callDetail.failureReason")}>
                        <span className="text-destructive">{call.failureReason}</span>
                      </InfoRow>
                    )}
                    {call.notes && (
                      <div className="col-span-2">
                        <InfoRow label={t("callDetail.notes")}>{call.notes}</InfoRow>
                      </div>
                    )}
                  </dl>

                  {call.audioQuality && <CallAudioQualityPanel quality={call.audioQuality} />}

                  <div>
                    <h2 className="mb-3 flex items-center gap-2 text-sm font-medium">
                      <Volume2 aria-hidden className="size-4 text-muted-foreground" />
                      {t("callDetail.recording")}
                    </h2>
                    <AudioPlayer
                      url={recording.data?.url ?? null}
                      downloadUrl={recording.data?.downloadUrl}
                      loading={recording.isLoading}
                    />
                    {call.recordingName && recording.data?.url ? (
                      <div className="mt-2">
                        <CallRecordingActions
                          callId={call.id}
                          canDelete={canDeleteRecording}
                          showDownload={false}
                          compact={false}
                          onDeleted={() => onOpenChange(false)}
                        />
                      </div>
                    ) : null}
                  </div>
                </section>

                <aside className="border-t border-border/70 bg-muted/15 px-5 py-5 sm:px-6 xl:min-h-0 xl:overflow-y-auto xl:border-t-0 xl:border-x">
                  <h2 className="mb-5 flex items-center gap-2 text-sm font-medium">
                    <Clock3 aria-hidden className="size-4 text-muted-foreground" />
                    {t("callDetail.timeline")}
                  </h2>
                  <ol>
                    {timeline.map((event, index) => (
                      <li key={event.id} className="relative flex gap-3 pb-5 last:pb-0">
                        {index < timeline.length - 1 && (
                          <span
                            aria-hidden
                            className="absolute top-3.5 bottom-0 left-[0.3125rem] w-px bg-border"
                          />
                        )}
                        <span
                          aria-hidden
                          className="relative mt-1.5 size-2.5 shrink-0 rounded-full border-2 border-primary bg-popover"
                        />
                        <div className="min-w-0">
                          <p className="font-medium">{t(`callDetail.events.${event.id}`)}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                            {formatDateTime(event.at)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </aside>

                <section className="border-t border-border/70 px-5 py-5 sm:px-6 xl:flex xl:min-h-0 xl:flex-col xl:border-t-0">
                  <h2 className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <FileText aria-hidden className="size-4 text-muted-foreground" />
                    {t("callDetail.transcription")}
                  </h2>
                  {!call.recordingName ? (
                    <div className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                      {t("callDetail.noTranscription")}
                    </div>
                  ) : transcription.isLoading ? (
                    <div
                      role="status"
                      aria-busy="true"
                      aria-label={t("callDetail.loadingTranscription")}
                      className="space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3"
                    >
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-4/5" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  ) : transcriptLines.length > 0 ? (
                    <div className="max-h-80 min-h-0 space-y-4 overflow-y-auto rounded-lg border border-border bg-muted/20 px-4 py-4 text-sm leading-relaxed xl:max-h-none xl:flex-1">
                      {transcriptLines.map((line) => (
                        <div key={line.id} className="grid grid-cols-[5.75rem_minmax(0,1fr)] gap-3">
                          <div className="space-y-1">
                            {line.startSeconds !== null && (
                              <p className="text-xs font-medium text-foreground tabular-nums">
                                {formatTranscriptTime(line.startSeconds)}
                                {line.endSeconds !== null && (
                                  <span className="text-muted-foreground">
                                    –{formatTranscriptTime(line.endSeconds)}
                                  </span>
                                )}
                              </p>
                            )}
                            <p className="text-xs font-semibold text-muted-foreground">
                              {t(`callDetail.speakers.${line.speaker}`)}
                            </p>
                          </div>
                          <p>{line.text}</p>
                        </div>
                      ))}
                      {transcriptLines.every((line) => line.startSeconds === null) && (
                        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
                          {t("callDetail.noTimestamps")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                      {t("callDetail.noTranscription")}
                    </div>
                  )}
                </section>
              </div>
            </div>

            {/* Ações no rodapé: depois de ler a ligação inteira (tabulação,
                gravação, transcrição) a mão já está embaixo — no topo o atalho
                ficava antes do motivo de usá-lo. "Fechar" explícito porque o X
                do canto é pequeno e some no meio do cabeçalho.
                O link do HubSpot só existe quando o back devolve a URL: sem
                portal configurado ou sem external id ela vem null, e botão
                morto é pior que botão nenhum. */}
            <DialogFooter
              closeLabel={t("ui.close")}
              className="m-0 rounded-none border-x-0 border-b-0 px-5 py-3 sm:justify-between sm:px-6"
            >
              <DialogClose render={<Button type="button" variant="ghost" />}>
                {t("callDetail.close")}
              </DialogClose>
              {lead.data?.hubspotUrl && (
                <Button
                  render={
                    <a href={lead.data.hubspotUrl} target="_blank" rel="noopener noreferrer" />
                  }
                  nativeButton={false}
                  role="link"
                  variant="outline"
                >
                  <ExternalLink className="size-4" />
                  {t("callDetail.openLeadHubSpot")}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
